import { computed } from 'mobx';
import { observer } from 'mobx-react';
import {
    autoinject,
    bind,
    CardItem,
    displayFormatTime,
    Enums,
    inject,
    StatusCard,
    TasksStore,
    Tooltip,
} from 'Mri.Web.Common';
import * as React from 'react';
import { BsArrowClockwise } from '@react-icons/all-files/bs/BsArrowClockwise';
import { BsArrowRepeat } from '@react-icons/all-files/bs/BsArrowRepeat';
import { ActiveDisplay } from '../../ActiveDisplay';
import { DisplaysStore } from '../../DisplaysStore';

export interface IDisplaySoftwareUpdateStatusCard {
    readonly display: ActiveDisplay;
    readonly loading?: boolean;
}

@autoinject
@observer
export class DisplaySoftwareUpdateStatusCard extends React.Component<IDisplaySoftwareUpdateStatusCard> {
    public render() {
        return (
            <div className="w-100">
                {this.show && (
                    <div className="col-12 col-md-6 mb-3">
                        <StatusCard
                            title={this.renderTitle()}
                            loading={this.props.loading}
                            className="software-update-card"
                        >
                            {this.status && this.status.pollTime && (
                                <CardItem
                                    label="Poll Time"
                                    value={
                                        <span>
                                            {displayFormatTime(
                                                this.status.pollTime,
                                                'M/d/yyyy h:mm a',
                                                this.props.display.serialNumber
                                            )}
                                        </span>
                                    }
                                />
                            )}

                            {this.status?.pendingFileSystemUpdates.map(fs => (
                                <CardItem
                                    key={fs.targetFileSystem.fileSystemGuid}
                                    label={fs.targetFileSystem.fileName}
                                    value={fs.status}
                                />
                            ))}
                        </StatusCard>
                    </div>
                )}
            </div>
        );
    }

    @bind
    private renderTitle() {
        return (
            <span>
                Software Update Status
                {this.status && (
                    <div className="d-inline-block float-end small">
                        <span className={this.stateClass}>
                            {Enums.toString(Enums.SoftwareUpdateState, this.status.state)}
                        </span>
                        {this.showClear && (
                            <Tooltip message="Reschedule">
                                <button onClick={this.clearUpdateStatus} className="btn btn-link ms-2">
                                    <BsArrowClockwise style={{ fontSize: 18 }} className="" />
                                </button>
                            </Tooltip>
                        )}
                        {this.updating && <BsArrowRepeat className="ms-2 fa-pulse" />}
                    </div>
                )}
            </span>
        );
    }

    @bind
    private async clearUpdateStatus() {
        const task = this.tasksStore.startSessionTask(`Clear ${this.props.display.serialNumber} status`);

        try {
            await this.displaysStore.clearSoftwareUpdateStatusAsync(this.props.display.serialNumber);

            task.complete();
        } catch (e: any) {
            task.error(e);
        }
    }

    @computed
    private get show() {
        return (
            this.status != null &&
            this.status.state != Mri.DisplaySoftware.SoftwareUpdateState.Idle &&
            this.status.state != Mri.DisplaySoftware.SoftwareUpdateState.Updated &&
            this.status.state == Mri.DisplaySoftware.SoftwareUpdateState.Scheduled
        );
    }

    @computed
    private get showClear() {
        return (
            this.status != null &&
            this.status.state != Mri.DisplaySoftware.SoftwareUpdateState.Idle &&
            this.status.state != Mri.DisplaySoftware.SoftwareUpdateState.Updating &&
            this.status.state != Mri.DisplaySoftware.SoftwareUpdateState.UpdatingSlave
        );
    }

    @computed
    private get updating() {
        return (
            this.status?.state == Mri.DisplaySoftware.SoftwareUpdateState.Pending ||
            this.status?.state == Mri.DisplaySoftware.SoftwareUpdateState.Updating ||
            this.status?.state == Mri.DisplaySoftware.SoftwareUpdateState.UpdatingSlave
        );
    }

    @computed
    private get stateClass() {
        if (this.status?.state == 4) {
            return 'text-danger';
        }
        return 'text-teal';
    }

    @computed
    private get status() {
        return this.displaysStore.updateStatuses.get(this.props.display.serialNumber);
    }

    @inject
    private readonly displaysStore!: DisplaysStore;
    @inject
    private readonly tasksStore!: TasksStore;
}
